import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Download as DownloadIcon } from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import TableWithTopScroll from "components/common/TableWithTopScroll";

import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  MenuItem,
  Dialog,
  Grid,
  Tooltip,
  Select,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  Badge as BadgeIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  DateRange as DateIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import {
  fetchEmployees,
  updateExistingEmployee,
} from "store/features/company/employee/employeeActions";
import {
  selectEmployees,
  selectEmployeeLoading,
} from "store/features/company/employee/employeeSelectors";
import AddEmployeeModal from "./AddEmployeeModal";
import EditEmployeeModal from "./EditEmployeeModal";
import employeesSvg from "assets/folders/employees.svg";
import viewDetailsSvg from "assets/modals/details.svg";
import usePermissions from "hooks/usePermissions";

import { fetchDepartments } from "store/features/company/department/departmentActions";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import { fetchDesignations } from "store/features/company/userManagement/designation/designationActions";
import { selectDesignations } from "store/features/company/userManagement/designation/designationSelectors";

const Employee = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const employees = useSelector(selectEmployees);
  const loading = useSelector(selectEmployeeLoading);
  const { hasPermission, hasAnyPermission } = usePermissions();

  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [designationFilter, setDesignationFilter] = useState("all");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingEmployee, setViewingEmployee] = useState(null);

  const [statusOverrides, setStatusOverrides] = useState({});

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const departments = useSelector(selectDepartments);
  const designations = useSelector(selectDesignations);

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchDepartments());
    dispatch(fetchDesignations());
  }, [dispatch]);

  const handleStatusToggle = async (employee) => {
    if (togglingStatus) return;
    setTogglingStatus(true);

    const currentStatus = statusOverrides[employee.id] ?? employee.status;
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    setStatusOverrides((prev) => ({ ...prev, [employee.id]: newStatus }));

    const formattedData = {
      employee_code: employee.employee_code,
      first_name: employee.first_name,
      email: employee.email,
      ...(employee.role_id && { role_id: Number(employee.role_id) }),
      ...(employee.last_name && { last_name: employee.last_name }),
      ...(employee.phone && { phone: employee.phone }),
      ...(employee.gender && { gender: employee.gender }),
      ...(employee.date_of_birth && {
        date_of_birth: employee.date_of_birth.includes("T")
          ? employee.date_of_birth.split("T")[0]
          : employee.date_of_birth,
      }),
      ...(employee.joining_date && {
        joining_date: employee.joining_date.includes("T")
          ? employee.joining_date.split("T")[0]
          : employee.joining_date,
      }),
      ...(employee.last_working_date && {
        last_working_date: employee.last_working_date.includes("T")
          ? employee.last_working_date.split("T")[0]
          : employee.last_working_date,
      }),
      ...(employee.department_id && {
        department_id: Number(employee.department_id),
      }),
      ...(employee.designation_id && {
        designation_id: Number(employee.designation_id),
      }),
      ...(employee.attendance_policy_id && {
        attendance_policy_id: Number(employee.attendance_policy_id),
      }),
      ...(employee.reporting_manager_id !== undefined &&
        employee.reporting_manager_id !== null && {
          reporting_manager_id: Number(employee.reporting_manager_id),
        }),
      ...(employee.employment_type && {
        employment_type: employee.employment_type,
      }),
      ...(employee.work_location && { work_location: employee.work_location }),
      ...(employee.bank_name && { bank_name: employee.bank_name }),
      ...(employee.bank_account_number && {
        bank_account_number: employee.bank_account_number,
      }),
      ...(employee.ifsc_code && { ifsc_code: employee.ifsc_code }),
      ...(employee.pan_number && { pan_number: employee.pan_number }),
      ...(employee.AadhaarNo && { AadhaarNo: employee.AadhaarNo }),
      ...(employee.uan_number && { uan_number: employee.uan_number }),
      ...(employee.pf_number && { pf_number: employee.pf_number }),
      ...(employee.esi_number && { esi_number: employee.esi_number }),
      ...(employee.blood_group && { blood_group: employee.blood_group }),
      ...(employee.current_address && {
        current_address: employee.current_address,
      }),
      ...(employee.permanent_address && {
        permanent_address: employee.permanent_address,
      }),
      ...(employee.emergency_contact_no && {
        emergency_contact_no: employee.emergency_contact_no,
      }),
      ...(employee.basic_salary && {
        basic_salary: Number(employee.basic_salary),
      }),
      ...(employee.ctc && { ctc: Number(employee.ctc) }),
      status: newStatus,
    };

    const result = await dispatch(
      updateExistingEmployee(employee.id, formattedData),
    );

    if (result.success) {
      dispatch(fetchEmployees());
    }
    setStatusOverrides((prev) => {
      const next = { ...prev };
      delete next[employee.id];
      return next;
    });

    setTogglingStatus(false);
  };

  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch =
      searchTerm === "" ||
      employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.employee_code
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      employee.phone?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || employee.status === statusFilter;

    const matchesDepartment =
      departmentFilter === "all" ||
      employee.department_id === parseInt(departmentFilter);

    const matchesDesignation =
      designationFilter === "all" ||
      employee.designation_id === parseInt(designationFilter);

    return (
      matchesSearch && matchesStatus && matchesDepartment && matchesDesignation
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      if (dateString.includes("T")) {
        const [datePart] = dateString.split("T");
        const [year, month, day] = datePart.split("-");
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
      }
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split("-");
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
      }
      return dateString;
    } catch (e) {
      return "-";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "-";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  const getEmploymentTypeColor = (type) => {
    const colors = {
      permanent: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      contract: {
        bg: "#dbeafe",
        text: "#1e40af",
        darkBg: "rgba(59,130,246,0.25)",
        darkText: "#93c5fd",
      },
      intern: {
        bg: "#f3e8ff",
        text: "#6b21a5",
        darkBg: "rgba(168,85,247,0.25)",
        darkText: "#d8b4fe",
      },
    };
    return colors[type] || colors.permanent;
  };

  const EmploymentTypeBadge = ({ type }) => {
    const colors = getEmploymentTypeColor(type);
    const label =
      {
        permanent: "Permanent",
        contract: "Contract",
        intern: "Intern",
      }[type] ||
      type ||
      "Permanent";

    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          "& .MuiChip-label": { px: 1 },
          ...(theme.palette.mode === "dark" && {
            backgroundColor: colors.darkBg,
            color: colors.darkText,
          }),
        }}
      />
    );
  };

  const StatusBadge = ({ status }) => {
    if (status === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label="Active"
          size="small"
          sx={{
            justifyContent: "flex-start",
            width: 90,
            px: 0.5,
            backgroundColor: "#d1fae5",
            color: "#0abe31",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "30px",
            ...(theme.palette.mode === "dark" && {
              backgroundColor: "rgba(41, 185, 16, 0.25)",
              color: "#6ee774",
            }),
          }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: 14 }} />}
        label="Inactive"
        size="small"
        sx={{
          justifyContent: "flex-start",
          width: 90,
          px: 0.5,
          backgroundColor: "#fee2e2",
          color: "#991b1b",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "30px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(239,68,68,0.25)",
            color: "#fca5a5",
          }),
        }}
      />
    );
  };

  const exportEmployeesToExcel = () => {
    if (!filteredEmployees || filteredEmployees.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Yellow header style
    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 12,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFFF00" }, // Changed to standard yellow
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["EMPLOYEES REPORT"]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 10 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // Summary
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Total Employees", filteredEmployees.length],
        [
          "Active Employees",
          filteredEmployees.filter((e) => e.status === "active").length,
        ],
        [
          "Inactive Employees",
          filteredEmployees.filter((e) => e.status === "inactive").length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 6;

    // Headers
    const headers = [
      "ID",
      "Employee Code",
      "First Name",
      "Last Name",
      "Email",
      "Phone",
      "Department",
      "Designation",
      "Employment Type",
      "Joining Date",
      "Last Working Date",
      "Status",
      "Created At",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    // Apply yellow header style to header row
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // Data
    filteredEmployees.forEach((employee) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            employee.id || "",
            employee.employee_code || "",
            employee.first_name || "",
            employee.last_name || "",
            employee.email || "",
            employee.phone || "",
            employee.department_name || "",
            employee.designation_name || "",
            employee.employment_type || "",
            employee.joining_date ? employee.joining_date.split("T")[0] : "",
            employee.last_working_date
              ? employee.last_working_date.split("T")[0]
              : "",
            employee.status || "",
            employee.created_at
              ? new Date(employee.created_at).toLocaleString()
              : "",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    // Column widths
    ws["!cols"] = Array(headers.length).fill({ wch: 20 });

    XLSX.utils.book_append_sheet(wb, ws, "Employees");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Employees_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={employeesSvg}
              alt="Employees"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Employees
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage your team members
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Export Excel Button - ADD THIS */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportEmployeesToExcel}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Excel
            </Button>
            {hasPermission("employee.create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Employee
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 3, flexWrap: "wrap" }}
      >
        <TextField
          placeholder="Search by name, email, employee code or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            width: { xs: "100%", sm: 250 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Department"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 180 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "primary.main",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  bgcolor: "background.paper",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Departments</MenuItem>
          {departments.map((dept) => (
            <MenuItem key={dept.id} value={dept.id}>
              {dept.name} ({dept.code})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Designation"
          value={designationFilter}
          onChange={(e) => setDesignationFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 180 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "primary.main",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  bgcolor: "background.paper",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Designations</MenuItem>
          {designations.map((desg) => (
            <MenuItem key={desg.id} value={desg.id}>
              {desg.name} ({desg.code})
            </MenuItem>
          ))}
        </TextField>

        <Stack direction="row" spacing={1}>
          {["all", "active", "inactive"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>

        {(departmentFilter !== "all" ||
          designationFilter !== "all" ||
          statusFilter !== "all" ||
          searchTerm !== "") && (
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setDepartmentFilter("all");
              setDesignationFilter("all");
              setPage(0);
            }}
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              color: "primary.main",
              borderColor: "#f59e0b",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#d97706",
                backgroundColor: "rgba(245,158,11,0.1)",
              },
            }}
            variant="outlined"
          >
            Clear All Filters
          </Button>
        )}
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading employees...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableWithTopScroll
            deps={[filteredEmployees.length, loading, rowsPerPage, page]}
          >
            {" "}
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    ID / Emp Code
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Employee Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Employee Details
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Employment Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Department
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Designation
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Shift
                  </TableCell>
                  {hasAnyPermission([
                    "employee.update",
                    "employee.view",
                    "employee.create",
                  ]) && (
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        color: "text.secondary",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <BadgeIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No employees found. Click "Add Employee" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((employee, index) => (
                      <TableRow
                        key={employee.id}
                        component={motion.tr}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        sx={{
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.5)"
                                : "#f9fafb",
                          },
                        }}
                      >
                        <TableCell>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                                fontSize: "0.875rem",
                                color: "primary.main",
                              }}
                            >
                              #{employee.id}
                            </Typography>

                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#ffffff"
                                    : "#686b72",
                              }}
                            >
                              {employee.employee_code}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              {employee.first_name} {employee.last_name}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <EmailIcon
                                sx={{ fontSize: 14, color: "#9ca3af" }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontStyle: "italic",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#d1d5db"
                                      : "#4b5563",
                                }}
                              >
                                {employee.email}
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <PhoneIcon
                                sx={{ fontSize: 14, color: "#9ca3af" }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontStyle: "italic",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#d1d5db"
                                      : "#4b5563",
                                }}
                              >
                                {employee.phone || "-"}
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <EmploymentTypeBadge
                            type={employee.employment_type}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {employee.department_name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {employee.designation_name || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {hasPermission("employee.update") ? (
                            <Select
                              value={
                                statusOverrides[employee.id] ?? employee.status
                              }
                              onChange={() => handleStatusToggle(employee)}
                              size="small"
                              variant="standard"
                              disableUnderline
                              disabled={togglingStatus}
                              renderValue={(value) => (
                                <Chip
                                  icon={
                                    value === "active" ? (
                                      <CheckCircleIcon sx={{ fontSize: 14 }} />
                                    ) : (
                                      <CancelIcon sx={{ fontSize: 14 }} />
                                    )
                                  }
                                  label={
                                    value === "active" ? "Active" : "Inactive"
                                  }
                                  size="small"
                                  sx={{
                                    justifyContent: "flex-start",
                                    width: 100,
                                    px: 0.5,
                                    backgroundColor:
                                      value === "active"
                                        ? theme.palette.mode === "dark"
                                          ? "rgba(41,185,16,0.25)"
                                          : "#d1fae5"
                                        : theme.palette.mode === "dark"
                                        ? "rgba(239,68,68,0.25)"
                                        : "#fee2e2",
                                    color:
                                      value === "active"
                                        ? theme.palette.mode === "dark"
                                          ? "#6ee774"
                                          : "#0abe31"
                                        : theme.palette.mode === "dark"
                                        ? "#fca5a5"
                                        : "#991b1b",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                    height: "30px",
                                  }}
                                />
                              )}
                              sx={{
                                minWidth: 120,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "& .MuiSelect-select": {
                                  p: 0,
                                  pr: "24px !important",
                                },
                                "& .MuiSelect-icon": {
                                  color:
                                    (statusOverrides[employee.id] ??
                                      employee.status) === "active"
                                      ? "#0abe31"
                                      : "#991b1b",
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: { borderRadius: "10px", mt: 0.5 },
                                },
                              }}
                            >
                              <MenuItem value="active">
                                <Chip
                                  icon={
                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                  }
                                  label="Active"
                                  size="small"
                                  sx={{
                                    justifyContent: "flex-start",
                                    width: 100,
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(41,185,16,0.25)"
                                        : "#d1fae5",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#6ee774"
                                        : "#0abe31",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                    height: "28px",
                                  }}
                                />
                              </MenuItem>
                              <MenuItem value="inactive">
                                <Chip
                                  icon={<CancelIcon sx={{ fontSize: 14 }} />}
                                  label="Inactive"
                                  size="small"
                                  sx={{
                                    justifyContent: "flex-start",
                                    width: 100,
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(239,68,68,0.25)"
                                        : "#fee2e2",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#fca5a5"
                                        : "#991b1b",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                    height: "28px",
                                  }}
                                />
                              </MenuItem>
                            </Select>
                          ) : (
                            <StatusBadge
                              status={
                                statusOverrides[employee.id] ?? employee.status
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            {/* <EventIcon
                              sx={{ fontSize: 14, color: "primary.main" }}
                            /> */}
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#d1d5db"
                                    : "#4b5563",
                              }}
                            >
                              {employee.shift_name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            <Tooltip title="View Details">
                              <IconButton
                                onClick={() => {
                                  setViewingEmployee(employee);
                                  setShowViewModal(true);
                                }}
                                size="small"
                                sx={{
                                  color: "#3b82f6",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(59,130,246,0.2)"
                                        : "#dbeafe",
                                  },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {hasPermission("employee.update") && (
                              <Tooltip title="Edit">
                                <IconButton
                                  onClick={() => {
                                    setSelectedEmployee(employee);
                                    setShowEditModal(true);
                                  }}
                                  size="small"
                                  sx={{
                                    color: "primary.main",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.mode === "dark"
                                          ? "rgba(245,158,11,0.2)"
                                          : "#fadc9f",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableWithTopScroll>
        </Paper>
      )}

      {/* Pagination */}
      {!loading && filteredEmployees.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredEmployees.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            mt: 2,
            color: "text.secondary",
            ".MuiTablePagination-selectIcon, .MuiTablePagination-actions button":
              {
                color: "text.secondary",
              },
          }}
        />
      )}

      {/* Empty State for No Search Results */}
      {!loading && filteredEmployees.length === 0 && employees.length > 0 && (
        <Paper
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            bgcolor: "background.paper",
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
          <Typography
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            No employees match your search criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
            }}
            sx={{
              color: "primary.main",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(245,158,11,0.1)"
                    : "#fffbeb",
              },
            }}
          >
            Clear filters
          </Button>
        </Paper>
      )}

      {/* View Details Modal  */}
      <Dialog
        open={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewingEmployee(null);
        }}
        maxWidth="md"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
        }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            maxWidth: "800px",
            margin: "16px",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                : "#ffffff",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        {viewingEmployee && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {/* Header with Gradient */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                p: 3,
                pr: 5,
                position: "relative",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="img"
                  src={viewDetailsSvg}
                  alt="Details"
                  sx={{
                    width: 50,
                    height: 50,
                    objectFit: "contain",
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                  }}
                />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "#ffffff",
                      letterSpacing: "0.5px",
                    }}
                  >
                    EMPLOYEE DETAILS
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontSize: "0.85rem",
                    }}
                  >
                    {viewingEmployee.first_name} {viewingEmployee.last_name} (
                    {viewingEmployee.employee_code})
                  </Typography>
                </Box>
              </Stack>

              <IconButton
                onClick={() => {
                  setShowViewModal(false);
                  setViewingEmployee(null);
                }}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  color: "#ffffff",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                maxHeight: "calc(80vh - 180px)",
                overflowY: "auto",
                overflowX: "hidden",
                p: 3,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#f59e0b",
                  borderRadius: "10px",
                },
              }}
            >
              <Stack spacing={3}>
                {/* Section 1: EMPLOYEE INFORMATION */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    EMPLOYEE INFORMATION
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Employee ID
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color: "primary.main",
                        }}
                      >
                        #{viewingEmployee.id}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Employee Code
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                        }}
                      >
                        {viewingEmployee.employee_code}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Full Name
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 500,
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.first_name} {viewingEmployee.last_name}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Gender
                      </Typography>
                      <Chip
                        label={
                          viewingEmployee.gender?.charAt(0).toUpperCase() +
                            viewingEmployee.gender?.slice(1) || "Other"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            viewingEmployee.gender === "male"
                              ? "#dbeafe"
                              : viewingEmployee.gender === "female"
                              ? "#fce4ec"
                              : "#f3e8ff",
                          color:
                            viewingEmployee.gender === "male"
                              ? "#1e40af"
                              : viewingEmployee.gender === "female"
                              ? "#b71c1c"
                              : "#6b21a5",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          height: "24px",
                          ...(theme.palette.mode === "dark" && {
                            backgroundColor:
                              viewingEmployee.gender === "male"
                                ? "rgba(59,130,246,0.25)"
                                : viewingEmployee.gender === "female"
                                ? "rgba(244,67,54,0.25)"
                                : "rgba(168,85,247,0.25)",
                            color:
                              viewingEmployee.gender === "male"
                                ? "#93c5fd"
                                : viewingEmployee.gender === "female"
                                ? "#ef9a9a"
                                : "#d8b4fe",
                          }),
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Date of Birth
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {formatDate(viewingEmployee.date_of_birth)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Blood Group
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.blood_group || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        gridColumn: "span 2",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Status
                      </Typography>
                      <StatusBadge status={viewingEmployee.status} />
                    </Box>
                  </Box>
                </Box>

                {/* Section 2: EMPLOYMENT DETAILS */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    EMPLOYMENT DETAILS
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Department
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.department_name || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Designation
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.designation_name || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Role
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.role_name || "-"} (
                        {viewingEmployee.role_code || ""})
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Employment Type
                      </Typography>
                      <EmploymentTypeBadge
                        type={viewingEmployee.employment_type}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Joining Date
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {formatDate(viewingEmployee.joining_date) || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Last Working Date
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {formatDate(viewingEmployee.last_working_date) || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Work Location
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.work_location || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        gridColumn: "span 2",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Reporting Manager
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.reporting_manager_name || "None"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        gridColumn: "span 2",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Shift / Policy
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.attendance_policy_name
                          ? `${viewingEmployee.attendance_policy_name}${
                              viewingEmployee.shift_name
                                ? ` (${viewingEmployee.shift_name}: ${viewingEmployee.shift_start_time} - ${viewingEmployee.shift_end_time})`
                                : ""
                            }`
                          : "Not Assigned"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Section 3: BANK & ID DETAILS */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    BANK & ID DETAILS
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Bank Name
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.bank_name || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Account Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.bank_account_number || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        IFSC Code
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.ifsc_code || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        PAN Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.pan_number || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        UAN Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.uan_number || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        PF Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.pf_number || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        ESI Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.esi_number || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        gridColumn: "span 2",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Aadhaar Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.AadhaarNo ||
                          viewingEmployee.aadhaar_no ||
                          "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Section 4: CONTACT INFORMATION */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    CONTACT INFORMATION
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.email || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Phone
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.phone || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Emergency Contact
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingEmployee.emergency_contact_no || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        gridColumn: "span 2",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Current Address
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                          textAlign: "right",
                        }}
                      >
                        {viewingEmployee.current_address || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        gridColumn: "span 2",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Permanent Address
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                          textAlign: "right",
                        }}
                      >
                        {viewingEmployee.permanent_address || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Section 5: TIMESTAMPS */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    TIMESTAMPS
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Created At
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatDateTime(viewingEmployee.created_at)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Updated At
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatDateTime(viewingEmployee.updated_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2.5,
                borderTop: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={() => {
                  setSelectedEmployee(viewingEmployee);
                  setShowViewModal(false);
                  setShowEditModal(true);
                }}
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{
                  borderRadius: "10px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderColor: "#f59e0b",
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "#d97706",
                    backgroundColor: "rgba(245,158,11,0.08)",
                  },
                }}
              >
                Edit Employee
              </Button>
              <Button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingEmployee(null);
                }}
                variant="contained"
                sx={{
                  borderRadius: "10px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </motion.div>
        )}
      </Dialog>

      {/* Modals */}
      <AddEmployeeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchEmployees())}
      />

      <EditEmployeeModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        onSuccess={() => dispatch(fetchEmployees())}
      />
    </Box>
  );
};

export default Employee;
